import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'

interface Language {
  code: string
  name: string
  nativeName: string
  flag: string
  direction: 'ltr' | 'rtl'
}

interface I18nContextType {
  language: string
  languages: Language[]
  translations: Record<string, string>
  setLanguage: (code: string) => void
  t: (key: string, fallback?: string) => string
  isLoading: boolean
}

const I18nContext = createContext<I18nContextType | undefined>(undefined)

const STORAGE_KEY = 'expandly_language'
const API_BASE = '/api/i18n'

export const I18nProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<string>(() => {
    return localStorage.getItem(STORAGE_KEY) || navigator.language.split('-')[0] || 'en'
  })
  const [languages, setLanguages] = useState<Language[]>([])
  const [translations, setTranslations] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(true)

  // Load supported languages
  useEffect(() => {
    const loadLanguages = async () => {
      try {
        const response = await fetch(`${API_BASE}/languages`)
        if (response.ok) {
          const data = await response.json()
          setLanguages(data)
        }
      } catch (err) {
        console.error('Failed to load languages:', err)
        // Fallback languages
        setLanguages([
          { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧', direction: 'ltr' },
          { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪', direction: 'ltr' },
          { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', direction: 'ltr' },
          { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', direction: 'ltr' },
        ])
      }
    }
    loadLanguages()
  }, [])

  // Load translations when language changes
  useEffect(() => {
    const loadTranslations = async () => {
      setIsLoading(true)
      try {
        const response = await fetch(`${API_BASE}/translations/${language}`)
        if (response.ok) {
          const data = await response.json()
          setTranslations(data)
        }
      } catch (err) {
        console.error('Failed to load translations:', err)
        // Keep existing translations
      } finally {
        setIsLoading(false)
      }
    }
    loadTranslations()
  }, [language])

  // Update document direction
  useEffect(() => {
    const currentLang = languages.find((l) => l.code === language)
    if (currentLang) {
      document.documentElement.dir = currentLang.direction
      document.documentElement.lang = language
    }
  }, [language, languages])

  const setLanguage = useCallback((code: string) => {
    setLanguageState(code)
    localStorage.setItem(STORAGE_KEY, code)
  }, [])

  const t = useCallback(
    (key: string, fallback?: string): string => {
      return translations[key] || fallback || key
    },
    [translations]
  )

  return (
    <I18nContext.Provider
      value={{
        language,
        languages,
        translations,
        setLanguage,
        t,
        isLoading,
      }}
    >
      {children}
    </I18nContext.Provider>
  )
}

export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext)
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider')
  }
  return context
}

// Utility hook for translation with TypeScript support
export const useTranslation = () => {
  const { t, language, setLanguage, languages, isLoading } = useI18n()
  return { t, language, setLanguage, languages, isLoading }
}
